import { seedgreenAPI } from "../api/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
/**
 * Query to validate a password reset token and get username.
 * Uses the standard API instance since the endpoint allows anonymous requests.
 */
export function useValidateResetToken(selector, verifier) {
    return useQuery({
        queryKey: ["validate-reset-token", selector, verifier],
        queryFn: async () => {
            if (!selector.value || !verifier.value) {
                throw new Error("Token is required");
            }
            const response = await seedgreenAPI.get("v2/forgotpassword/ValidateResetToken", {
                params: {
                    sel: selector.value,
                    ver: verifier.value,
                },
            });
            return response.data;
        },
        enabled: computed(() => !!selector.value && !!verifier.value),
        staleTime: 0, // Don't cache reset token validation results
        gcTime: 0, // Don't keep in cache after component unmounts
        retry: false, // Don't retry failed requests
    });
}
/**
 * Mutation to reset a user's password using a valid reset token
 */
export function useResetPassword() {
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (params) => {
            await seedgreenAPI.post(`v2/forgotpassword/ResetPassword`, {
                selector: params.selector,
                verifier: params.verifier,
                password: params.password,
            });
        },
        // Note: We don't invalidate queries here since this is typically
        // followed by a redirect to login
    });
    return {
        resetPassword: mutateAsync,
        isLoading: isPending,
        error,
    };
}
/**
 * Mutation to get a password reset link for a user (admin/manager feature).
 * Returns null if the user is rate limited.
 */
export function useGetResetLink() {
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (userId) => {
            const response = await seedgreenAPI.get(`v2/ForgotPassword/GetResetLink`, {
                params: { userId },
            });
            return response.data;
        },
    });
    return {
        getResetLink: mutateAsync,
        isLoading: isPending,
        error,
    };
}
